import { GoAButton, GoAButtonGroup, GoACheckbox, GoAFormItem, GoAInput, GoAInputText, GoAModal, GoARadioGroup, GoARadioItem } from "@abgov/react-components";
import PageHeader from "../../components/PageHeader";
import { useNavigate } from "react-router-dom";
import { SetStateAction, useCallback, useEffect, useState } from "react";
import { SelectOptions } from "../../model/Base";
import { useRecoilState } from "recoil";
import { forestAreaState } from "../../states/Order.state";
import { Autocomplete, Breadcrumbs, Link, debounce, TextField, Typography } from "@mui/material";
import { ExternalService, ISearch } from "../../services/ExternalService";
import { Order } from "../../model/Order";
import { CustomFmF } from "../../components/datePicker";
import { useAuth } from "react-oidc-context";
import { getUserName } from "../../utils/tokens";
import { forestAreaMap, orderFromOptions, orderTypeOptions } from "../../common/constants";
import { DatePicker, LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { OrderService } from "../../services/OrderService";
import { CalloutVariant } from "../../model/enums/Constants";
import dayjs, { Dayjs } from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import EmailPicker from "../../components/EmailPicker";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EmailPickerSingle from "../../components/EmailPickerSingle";
import UserNamePicker from "../../components/UserNamePicker";
import { clearSessionStorage, getDataFromSessionStorage, saveDataToSessionStorage } from "../../utils/SessionStorageUtils";
import PageLoader from "../../components/PageLoader";

interface IOrderDetailProps { }
const OrderNew: React.FC<IOrderDetailProps> = (props) => {
  const navigate = useNavigate();
  const [formState, setFormState] = useState<FormState>({
    order: new Order({
      fireNumber: getDataFromSessionStorage("fireNumberSaved"),
      forestArea: getDataFromSessionStorage("forestAreaSaved"),
      isOrderAssociatedWithFireNumber: getDataFromSessionStorage("isOrderAssociatedWithFireNumberSaved"),
      shipTo: getDataFromSessionStorage("shipToSaved"),
      orderFrom: getDataFromSessionStorage("orderFromSaved"),
      deliveryMethod: getDataFromSessionStorage("deliveryMethodSaved"),
      phoneNumber: getDataFromSessionStorage("phoneNumberSaved"),
    }),
  });
  const [shipToCharCount, setShiptoCharCount] = useState<number>(Number(process.env.REACT_APP_TEXTAREA_CHARACTER_COUNT));
  const [fireNumberAvailable, setFireNumberAvailable] = useState(formState.order.isOrderAssociatedWithFireNumber);
  const [orderOnBehalf, setOrderOnBehalf] = useState(false);

  const [userName, setUserName] = useState('');
  const [showNotificationMessage, setShowNotificationMessage] =
    useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [modalHeading] = useState("Create order");
  const [calloutVariant, setCalloutVariant] = useState(CalloutVariant.SUCCESS);
  const [orderingForEmail, setOrderingForEmail] = useState<string>('');
  const [watcherError, setWatcherError] = useState("");

  interface FormState {
    order: Order;
  }


  const [forestAreaOptions, setForestAreaOptions] = useRecoilState<SelectOptions[]>(forestAreaState);

  const [showEmailSection, setShowEmailSection] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(
    null
  );
  const [selectedTime, setSelectedTime] = useState<Dayjs | null>(
    null
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [orderDate, setOrderDate] = useState<Date | null>(
    null
  );
  const [emailError, setEmailError] = useState("");
  const [orderingForNameValidationError, setOrderingForNameValidationError] = useState("");
  const [orderingForEmailValidationError, setOrderingForEmailValidationError] = useState<string>("");
  const [forestAreaManualOverride, setForestAreaManualOverride] = useState(false);
  const [fireNumber, setFireNumber] = useState("");
  const [fireNumberOptions, setFireNumberOptions] = useState<SelectOptions[]>([]); // Empty initially
  const [crewNumberOptions, setCrewNumberOptions] = useState<any[]>([]); // Empty initially
  const [crewTypeOptions, setCrewTypeOptions] = useState<any[]>([]); // Empty initially
  const [userSelectedList, setUserSelectedList] = useState<string>('');
  const [selectedWatcherList, setSelectedWatcherList] = useState<string>('');
  const [loading, setIsLoading] = useState(false);

  useEffect(() => {
    let fireNumberSaved = getDataFromSessionStorage("fireNumberSaved"); // Retrieve order from session storage

    if (fireNumberSaved) {
      setFireNumberOptions([
        {
          label: fireNumberSaved,
          value: fireNumberSaved,
        }
      ]);
    }

    let forestAreaSaved = getDataFromSessionStorage("forestAreaSaved");
    if (forestAreaSaved) {
      setForestAreaManualOverride(true);
    }
  }, [setForestAreaOptions]);


  useEffect(() => {

    let selectedForestArea = '';
    if (!forestAreaManualOverride) {
      const forestArekey = fireNumber.charAt(0);
      const forestArea = forestAreaMap[forestArekey] || null;
      selectedForestArea = forestAreaOptions?.find((x) => x.value === forestArea)?.value || '';
    } else {
      selectedForestArea = forestAreaOptions?.find((x) => x.value === formState.order.forestArea)?.value || '';

    }

    if (selectedForestArea.length > 0)
      formState.order.forestArea = selectedForestArea;

  }, [forestAreaOptions, fireNumber, formState.order, forestAreaManualOverride, fireNumberOptions]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const loadCrewTypes = async (inputValue: string) => {
      const crewTypes = await ExternalService.getCrewTypes(inputValue) || [];
      // Pass the transformed options to the callback function
      return crewTypes;
    }
    loadCrewTypes("").then((result: any) => {
      if (result) {
        const options = result?.map((r: any) => ({
          label: `${r.crewTypeName}`,
          value: r.crewTypeId,
          oracleId: r.oracleId
        }));
        setCrewTypeOptions(options);
      }
    });
  }, []); // Empty dependency array to run only once on mount

  const auth = useAuth();

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const validateForm = useCallback(
    (name: string = "", value: any | string[] = "") => {
      const newErrors: Record<string, string> = {};

      if ((name === "save" && fireNumberAvailable && !formState?.order.fireNumber) || (name === "fireNumber" && fireNumberAvailable && !value)) {
        newErrors["fireNumber"] = "Fire number is required";
      }

      if (name === "fireNumberAvailable" && value && !formState?.order.fireNumber) {
        newErrors["fireNumber"] = "Fire number is required";
      }

      if ((name === "save" && !formState?.order.forestArea) || (name === "forestArea" && !value)) {
        newErrors["forestArea"] = "Forest area is required";
      }

      if ((name === "save" && !formState?.order.orderType) || (name === "orderType" && !value)) {
        newErrors["orderType"] = "Order type is required";
      }

      if ((name === "save" && !formState?.order.orderFrom) || (name === "orderFrom" && !value)) {
        newErrors["orderFrom"] = "Submit order to is required";
      }

      if ((name === "save" && formState.order.deliveryMethod === "Ship to" && !formState?.order.shipTo) || (name === "shipTo" && formState.order.deliveryMethod === "shipTo" && !value)) {
        newErrors["shipTo"] = "Ship to is required";
      }


      if ((name === "save" && !selectedDate)) {
        newErrors["requiredByDate"] = "Required date is required";
      }

      if ((name === "save" && !selectedTime)) {
        newErrors["requiredByTime"] = "Required time is required";
      }

      if (formState?.order.requiredBy) {
        const requiredByDate = name === "requiredBy" ? new Date(value) : new Date(formState?.order.requiredBy);
        const orderDate = formState?.order.orderDate;

        if ((name === "save" && requiredByDate < new Date()) || (name === "requiredBy" && requiredByDate < new Date())) {
          newErrors["requiredByDate"] = "Required Date and time cannot be in the past";
        } else if (orderDate && requiredByDate < orderDate) {
          newErrors["requiredByDate"] = "Required Date and time cannot be before the Order Date";
        }
      }

      if (emailError) {
        newErrors["emailAddress"] = emailError;
      }

      if (orderingForEmailValidationError) {
        newErrors["orderingForEmail"] = orderingForEmailValidationError;
      }

      if (orderOnBehalf && !formState?.order.orderingForName) {
        newErrors["orderingForName"] = "Name is required";
      }

      if (orderOnBehalf && orderingForNameValidationError) {
        newErrors["orderingForName"] = orderingForNameValidationError;
      }

      if (orderOnBehalf && !formState?.order.orderingForEmail) {
        newErrors["orderingForEmail"] = "Email address is required";
      }

      if (name === "save" && showEmailSection && !formState?.order.emailAddress) {
        newErrors["emailAddress"] = "Email address is required";
      }

      if (name === "save" && watcherError.length > 0) {
        newErrors["watcherError"] = watcherError;
      }




      setErrors(newErrors);
      return Object.keys(newErrors).length === 0; // Return true if there are no errors
    },
    [formState, fireNumberAvailable, showEmailSection, emailError, watcherError, setErrors, orderOnBehalf, orderingForNameValidationError, orderingForEmailValidationError, selectedDate, selectedTime]
  );

  useEffect(() => {
    document.body.style.overflowX = "hidden";
    return () => {
      document.body.style.overflowX = "auto"; // Reset on unmount
    };
  }, []);

  useEffect(() => {
    validateForm();
  }, [emailError, validateForm]);


  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener("resize", handleResize);

    if (isMobile) {
      document.body.style.overflowX = "hidden";
    } else {
      document.body.style.overflowX = "auto"; // Reset for larger screens
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      document.body.style.overflowX = "auto"; // Cleanup on unmount
    };
  }, [isMobile]);

  useEffect(() => {
    const fetchForestAreaOptions = async () => {
      try {
        const transformToOptions = (inputArray: any[], useLookupFields = false) =>
          inputArray.map((item) => ({
            label: useLookupFields ? item.value : item.name,
            value: useLookupFields ? item.id : item.name,
          }));

        const objISearch: ISearch = {
          search: "",
          filterBy: { columnName: "CRT_ID", columnValue: "4" },
          pagination: { perPage: 20, page: 1 },
        };

        const response = await ExternalService.getCorporateRegion(objISearch);
        setForestAreaOptions(transformToOptions(response.data));
      } catch (e) {
        console.error("Error fetching regions:", e);
      }
    };

    if (!forestAreaOptions)
      fetchForestAreaOptions();

  }, [forestAreaOptions, setForestAreaOptions]);


  useEffect(() => {
    if (!userName) {
      setUserName(getUserName(auth?.user?.access_token ?? ""));
    }
    setShowEmailSection(
      formState.order.orderFrom === "Incident Logistics" || formState.order.orderFrom === "Other"
    );
  }, [formState.order.orderFrom, auth?.user?.access_token, userName]);

  function setValue(value: Partial<Order>) {
    setErrors({});
    setFormState(prevState => {
      const updatedOrder = new Order({
        ...prevState.order,
        ...value
      });

      console.log(updatedOrder); // Log the updated formPart

      return { ...prevState, order: updatedOrder };
    });
  }


  const handleFieldChange = (name: string, value: any) => {

    if (name === 'fireNumber') {
      setForestAreaManualOverride(false);
    }
    setValue({ [name]: value });
    validateForm(name, value);
    setFormChanged(true);
  };

  const handleCrewTypeChange = (crewType: any) => {
    handleFieldChange('crewTypeId', crewType ? Number(crewType.value) : null);
    handleFieldChange('crewTypeName', crewType ? crewType.label : '');
    setCrewNumberOptions([]);
    handleFieldChange('crewNumber', '');
  }

  const handleCrewNumberChange = (crewNumber: any) => {
    handleFieldChange('crewNumber', crewNumber);
    if (!formState.order.crewTypeId) {
      const crewTypeOracleId = Number(crewNumberOptions.find(i => String(i.value) === String(crewNumber))?.crewType);
      if (crewTypeOracleId) {
        handleFieldChange('crewTypeId', Number(crewTypeOptions.find(i => String(i.oracleId) === String(crewTypeOracleId))?.value));
        handleFieldChange('crewTypeName', crewTypeOptions.find(i => String(i.oracleId) === String(crewTypeOracleId))?.label);
      }
    }
  }

  const handleOrderOnBehalf = () => {
    setOrderOnBehalf(!orderOnBehalf);
    handleFieldChange("isOrderingForOtherPerson", !orderOnBehalf)
  };


  const handleOrderDateChange = (value: any) => {
    const inputDate = new Date(value.target.value);

    if (isNaN(inputDate.getTime())) {
      console.error("Invalid date format");
      return;
    }

    const selectedDate = dayjs(inputDate)
      .tz("America/Edmonton", true) // Treat the input date as already in America/Edmonton timezone
      .set('hour', 0)
      .set('minute', 1)
      .set('second', 0)
      .set('millisecond', 0).toDate();


    setOrderDate(inputDate);
    handleFieldChange("orderDate", selectedDate);
  };

  const onCrewNumberSearchChange = (inputValue: string) => {

    const lowerCasedInput = inputValue.trim().toLowerCase();
    const crewType = crewTypeOptions.find(i => i.value === formState.order.crewTypeId)?.oracleId;
    loadCrewNumbers(lowerCasedInput, crewType, (result: any) => {
      if (result) {
        const options = result?.map((r: any) => ({
          label: `${r.crewNumber}`,
          value: `${r.crewNumber}`,
          crewType: r.crewTypeId // Include crewTypeId for further processing if needed
        }));
        setCrewNumberOptions(options);
      }
    });
  };

  const onFireNoSearchChange = (inputValue: string) => {

    const lowerCasedInput = inputValue.toLowerCase();
    loadFireOptions(lowerCasedInput, (result: any) => {
      if (result) {
        const options = result?.map((r: any) => ({
          label: `${r.fireNumber}-${r.year}`,
          value: `${r.fireNumber}-${r.year}`
        }));
        setFireNumberOptions(options);
      }
    });
  };

  const loadFireOptions = debounce(async (inputValue: string, callback: (data: any[]) => void) => {
    if (!inputValue) return;
    const currentYearFireNumbers = await ExternalService.getFireNumbers(inputValue) || [];
    // Pass the transformed options to the callback function
    callback(currentYearFireNumbers);
  }, 500);


  const loadCrewNumbers = debounce(async (inputValue: string, crewType: number, callback: (data: any[]) => void) => {
    if (!inputValue) return;
    const crewNumbers = await ExternalService.getCrewNumbers(inputValue, crewType) || [];
    // Pass the transformed options to the callback function
    callback(crewNumbers);
  }, 500);




  const handleNextStep = async () => {
    setIsLoading(true);
    saveDataToSessionStorage("fireNumberSaved", formState.order.fireNumber);
    saveDataToSessionStorage("forestAreaSaved", formState.order.forestArea);
    saveDataToSessionStorage("isOrderAssociatedWithFireNumberSaved", formState.order.isOrderAssociatedWithFireNumber);
    saveDataToSessionStorage("shipToSaved", formState.order.shipTo);
    saveDataToSessionStorage("orderFromSaved", formState.order.orderFrom);
    saveDataToSessionStorage("deliveryMethodSaved", formState.order.deliveryMethod);
    saveDataToSessionStorage("phoneNumberSaved", formState.order.phoneNumber);


    if (!validateForm('save')) {
      console.log("Form contains errors. Cannot submit.");
      setIsLoading(false);
      return;
    }
    try {

      formState.order.orderBy = userName;
      //formState.order.isOrderingForOtherPerson = orderOnBehalf;
      let result = await OrderService.create(formState.order)
      if (result && result.success) {
        navigate('/order/orderNumber/' + result.orderNumber, { state: { redirectedFrom: 'orderNew' } });
      } else {
        setCalloutVariant(CalloutVariant.EMERGENCY);
        setNotificationMessage(result.error);
        setShowNotificationMessage(true);
      }

      setIsLoading(false);
    } catch (error) {
      console.error("Error updating or creating catalog:", error);
      alert("An error occurred while updating or creating the catalog");
    }
  }

  const handleDateChange = (date: Dayjs | null) => {
    setSelectedDate(date);
    updateDateTime(date, selectedTime);
  };

  const handleTimeChange = (time: Dayjs | null) => {
    if (time) {
      setSelectedTime(time);
      updateDateTime(selectedDate, time);
    }
  };

  const updateDateTime = (date: Dayjs | null, time: Dayjs | null) => {
    if (date && time) {
      // Format the date and time
      const formattedDate = date.format("YYYY-MM-DD");
      const formattedTime = time.format("HH:mm:ss"); // Ensure time is in 24-hour format

      // Combine formatted date and time into a single ISO string
      const combinedDateTime = dayjs.tz(`${formattedDate} ${formattedTime}`, "YYYY-MM-DD HH:mm", "America/Edmonton");
      //const combinedDateTime = new Date(`${formattedDate}T${formattedTime}`);

      //if (!isNaN(combinedDateTime.getTime())) {
      handleFieldChange("requiredBy", combinedDateTime);
      // } else {
      //   console.error("Invalid date or time combination");
      // }
    }
  };



  function handleUserSelectedListChange(value: SetStateAction<string>): void {
    setUserSelectedList(value);
    handleFieldChange('emailAddress', value);
  }

  function handleSelectedWatcherListChange(value: SetStateAction<string>): void {
    setSelectedWatcherList(value);
    handleFieldChange('watchers', value);
  }

  function handleOrderingForEmailChange(value: SetStateAction<string>): void {
    setOrderingForEmail(value);
    handleFieldChange('orderingForEmail', value);
  }

  function handleOrderingForNameChange(label: string, value?: string): void {
    handleFieldChange('orderingForName', label);
    let emailValue = value || orderingForEmail || '';
    setOrderingForEmail(emailValue);
    handleFieldChange('orderingForEmail', emailValue);
  }


  function handleClearAll(): void {
    // Update state values to empty strings

    const order = new Order();
    setFormState((prevState) => ({
      order: order,
    }));

    setOrderDate(null);
    setOrderOnBehalf(false);
    setOrderingForEmail("");
    setSelectedDate(null);
    setSelectedTime(null);
    setForestAreaManualOverride(true);
    setUserSelectedList('');
    setSelectedWatcherList('');

    // Clear session storage 
    clearSessionStorage("fireNumberSaved");
    clearSessionStorage("forestAreaSaved");
    clearSessionStorage("isOrderAssociatedWithFireNumberSaved");
    clearSessionStorage("shipToSaved");
    clearSessionStorage("orderFromSaved");
    clearSessionStorage("deliveryMethodSaved");
    clearSessionStorage("phoneNumberSaved");
  }


  // const [loading, setLoading] = useState(false);
  //const navigate = useNavigate();
  return (
    <>
      <PageLoader visible={loading} />
      <div className="content-padding">
        <div className="left width100">
          <Breadcrumbs aria-label="breadcrumb" separator={<ArrowForwardIcon fontSize="small" />}>
            <Link
              variant="body2"
              onClick={() => (window.location.pathname = '/ordersList')}
              style={{ cursor: 'pointer' }}
            >
              Home
            </Link>
            <Typography color="textPrimary">Create order</Typography>
          </Breadcrumbs>
        </div>
        <div className="left width100">
          <div className="left width-auto">
            <PageHeader title={'Create order'} />
          </div>
        </div>
        <div className='form-detail-container orders'>
          <div className="wrap-label-container">
            <label className="wrap-label">Is there a fire number associated with this order?</label>
            <GoARadioGroup
              name="item"
              value={fireNumberAvailable ? "1" : "0"}
              onChange={(_, value) => {
                handleFieldChange('isOrderAssociatedWithFireNumber', value === "1" ? true : false);
                setFireNumberAvailable(value === "1");
                validateForm("fireNumberAvailable");
                if (value === "0")
                  formState.order.fireNumber = "";
              }}
            >
              <GoARadioItem value="1" label="Yes"></GoARadioItem>
              <GoARadioItem value="0" label="No"></GoARadioItem>
            </GoARadioGroup>
          </div>

          <div className='input-form-item'>
            <GoAFormItem label="Fire number" error={errors['fireNumber']}>
              <Autocomplete
                disabled={!fireNumberAvailable}
                options={fireNumberOptions}
                getOptionLabel={(option) => option.label}
                value={
                  fireNumberAvailable ? fireNumberOptions?.find((x) => x.value === formState.order.fireNumber) || null : null}
                onChange={(event, newValue) => {
                  setForestAreaManualOverride(false);
                  handleFieldChange('fireNumber', newValue?.value)
                  setFireNumber(newValue?.value ?? '');
                }}
                onInputChange={(event: any, newInputValue) =>
                  onFireNoSearchChange(
                    newInputValue.includes('-') ? newInputValue.split('-')[0] : newInputValue
                  ) // remove the year part if entered
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Enter fire number"
                    onKeyDown={(e) => {
                      if (e.key === ' ') {
                        e.preventDefault(); // Block space character
                      }
                    }}
                  />
                )}
                className="order-create-input"
                sx={{
                  '& .MuiInputBase-root': {
                    height: 45, // Adjust the height here
                    backgroundColor: !fireNumberAvailable ? 'var(--goa-color-greyscale-100)' : 'white', // Greyed out when disabled
                    pointerEvents: !fireNumberAvailable ? 'none' : 'auto', // Prevent interaction when disabled
                  },
                }}
              />
            </GoAFormItem>
          </div>
          <div className='input-form-item'>
            <GoAFormItem label="Forest area" error={errors['forestArea']} helpText={"Forest area where the order is being submitted to"}>
              <Autocomplete
                className="order-create-input"
                options={forestAreaOptions || []}
                getOptionLabel={(option) => option.label || ''}
                value={forestAreaOptions?.find(
                  (x) => x.value === formState.order.forestArea
                ) || null}
                onChange={(event, newValue) => {
                  handleFieldChange('forestArea', newValue?.value)
                  setForestAreaManualOverride(true);
                }}
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" placeholder="Select forest area" />
                )}
                sx={{
                  '& .MuiInputBase-root': {
                    height: 45, // Adjust the height here
                  },
                }}
              />

            </GoAFormItem>
          </div>
          <div className='input-form-item' >
            <GoAFormItem label="Order date" error={errors['orderDate']}>
              <CustomFmF width={isMobile ? "355px" : "651px"} icon={formState.order.orderDate ? 'close' : 'calendar'} format="yyyy-MM-dd" value={orderDate} onChange={(value: Date | null) => handleOrderDateChange(value)} onBlur={(val: any) => { }} />
            </GoAFormItem>
          </div>
          <div className='input-form-item order-create-input'>
            <GoAFormItem label="Ordered by">
              <GoAInputText
                disabled
                width="100%"
                name="orderedBy"
                value={userName}
                maxLength={100}
                onChange={handleFieldChange} // Use handleFieldChange as event handler
              />
            </GoAFormItem>
          </div>
          <div className='input-form-item order-create-input'>
            <GoACheckbox
              name="isOrderingForOtherPerson"
              text="Order on behalf of someone else"
              onChange={
                handleOrderOnBehalf
              }
              checked={orderOnBehalf}
            />
          </div>

          {orderOnBehalf &&
            <div className="onbehalf-div">
              <div className='input-form-item order-create-input onbehalf'>
                <GoAFormItem label="Name" error={errors['orderingForName']}>
                  <UserNamePicker
                    handleOrderingForNameChange={handleOrderingForNameChange}
                    setValidationError={setOrderingForNameValidationError}
                  />
                </GoAFormItem>
              </div>
              <div className='input-form-item order-create-input onbehalf'>
                <GoAFormItem label="Email" error={errors['orderingForEmail']}>
                  <EmailPickerSingle
                    selectedEmail={orderingForEmail}
                    setSelectedEmail={handleOrderingForEmailChange}
                    setValidationError={setOrderingForEmailValidationError}
                  />
                </GoAFormItem>
              </div>
              <div className='input-form-item order-create-input onbehalf'>

                <GoAFormItem label="Phone" error={errors['orderingForPhone']} requirement="optional">
                  <GoAInput
                    type="tel"
                    width="100%"
                    maxLength={25}
                    value={''}
                    name="orderingForPhone"
                    onChange={handleFieldChange}
                  />
                </GoAFormItem>
              </div>
            </div>
          }
          <div className='input-form-item'>
            <GoAFormItem label="Crew type" requirement="optional">
              <Autocomplete
                //  freeSolo // Allow free text input
                options={crewTypeOptions}
                getOptionLabel={(option) => option.label}
                value={
                  crewTypeOptions?.find((x) => x.value === formState.order.crewTypeId) || null
                }
                onChange={(event, newValue) => {
                  handleCrewTypeChange(newValue);
                }}
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" placeholder="Select crew type" />
                )}
                className="order-create-input"
                sx={{
                  '& .MuiInputBase-root': {
                    height: 45,
                  },
                }}
              />
            </GoAFormItem>
          </div>
          <div className='input-form-item'>
            <GoAFormItem label="Crew number" requirement="optional">
              <Autocomplete
                freeSolo // Allow free text input
                options={crewNumberOptions}
                value={
                  crewNumberOptions?.find((x: any) => x.value === formState.order.crewNumber) ??
                  (formState.order.crewNumber ? { label: formState.order.crewNumber, value: formState.order.crewNumber } : '')
                }
                onChange={(event, newValue) => {
                  handleCrewNumberChange(newValue?.value ?? '');
                }}
                onInputChange={(event: any, newInputValue) => {
                  if (event && event.type === 'change') {
                    onCrewNumberSearchChange(newInputValue);
                  }
                  handleFieldChange('crewNumber', newInputValue);
                }}
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" placeholder="Enter crew number" />
                )}
                className="order-create-input"
                sx={{
                  '& .MuiInputBase-root': {
                    height: 45,
                  },
                }}
              />
            </GoAFormItem>
          </div>
          <div className='input-form-item '>
            <GoAFormItem label="Order type" error={errors['orderType']}>
              <Autocomplete

                options={orderTypeOptions}
                getOptionLabel={(option) => option.label}
                value={
                  orderTypeOptions?.find((x) => x.value === formState.order.orderType) || null
                }
                onChange={(event, newValue) => {
                  handleFieldChange('orderType', newValue?.value); // Update the selected fire number on change
                }}

                renderInput={(params) => (
                  <TextField {...params} variant="outlined" placeholder="Select order type" />
                )}
                className="order-create-input order-type"
                sx={{
                  '& .MuiInputBase-root': {
                    height: 45, // Adjust the height here
                  },
                  "& MuiInputBase-input": { fontWeight: "100 !important" },
                  "& MuiOutlinedInput-input": { fontWeight: "100 !important" },
                  "& input": { fontWeight: "100 !important" },
                }}
              />
            </GoAFormItem>
          </div>
          <div className='input-form-item'>
            <GoAFormItem label="Submit order to" error={errors['orderFrom']}>
              <Autocomplete
                className="order-create-input"
                options={orderFromOptions}
                getOptionLabel={(option) => option.label}
                value={
                  orderFromOptions?.find((x) => x.value === formState.order.orderFrom) || null
                }
                onChange={(event, newValue) => {
                  handleFieldChange('orderFrom', newValue?.value); // Update the selected fire number on change
                }}
                disableClearable={false} // Ensures the clear button is enabled
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" placeholder="Select submit order to" />
                )}

                sx={{
                  '& .MuiInputBase-root': {
                    height: 45, // Adjust the height here
                  }
                }}
              />

            </GoAFormItem>
          </div>
          {showEmailSection &&
            <div className='input-form-item order-create-input'>
              <GoAFormItem label="Email" error={errors['emailAddress']} helpText="Enter the email address(es) you want the order sent to">
                <EmailPicker
                  userSelectedList={userSelectedList}
                  setUserSelectedList={handleUserSelectedListChange}
                  setEmailValidationError={setEmailError}
                />
              </GoAFormItem>
            </div>
          }
          <div className='input-form-item'>
            <GoAFormItem label="Delivery methods" error={errors['deliveryMethod']}>
              <GoARadioGroup name="deliveryMethod" value={formState.order.deliveryMethod ?? "Pickup"}
                onChange={(name, value) => handleFieldChange(name, value)}>
                <GoARadioItem value="Pickup" label="Pickup"></GoARadioItem>
                <GoARadioItem value="Ship to" label="Ship to"></GoARadioItem>
              </GoARadioGroup>
            </GoAFormItem>
          </div>
          <div className='input-form-item order-create-input'>
            <GoAFormItem label="Enter address or pickup note" error={errors['shipTo']}>
              <textarea
                // disabled={formState.order.deliveryMethod !== "Ship to"}
                name="shipTo"
                value={formState.order.shipTo}
                placeholder="[name, address, city, province, postal code, coordinates, google maps link]"
                onChange={(e) => {
                  handleFieldChange("shipTo", e.currentTarget.value);
                  setShiptoCharCount(Number(process.env.REACT_APP_TEXTAREA_CHARACTER_COUNT) - e.currentTarget.value.length);
                }}
                className='order-create-input'
                maxLength={Number(process.env.REACT_APP_TEXTAREA_CHARACTER_COUNT)}
                rows={5}
              />

              <div style={{ width: isMobile ? "355px" : "651px" }}><label className="workorder-char-count-label">{`${shipToCharCount} characters remaining`}</label></div>
            </GoAFormItem>
          </div>
          <div className='input-form-item'>
            <GoAFormItem label="Phone" requirement="optional">
              <div style={{ width: isMobile ? "355px" : "651px" }}>
                <TextField
                  variant="outlined"
                  fullWidth
                  value={formState.order.phoneNumber}
                  onChange={(e) => handleFieldChange("phoneNumber", e.target.value)}
                  placeholder="Enter your phone number"
                  inputProps={{
                    maxLength: 25,
                  }}
                />
              </div>
            </GoAFormItem>
          </div>
          <div
            className="input-form-item"
            style={{ width: isMobile ? "355px" : "651px" }}
          >
            <div className="form-row no-wrap">
              <div className="item-space">
                <GoAFormItem label="Required date" error={errors['requiredByDate']}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      minDate={dayjs()}
                      value={selectedDate}
                      onChange={handleDateChange}
                      format="YYYY-MM-DD"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                        },
                      }}
                    />
                  </LocalizationProvider>
                </GoAFormItem>
              </div>
              <div className="item-space">
                <GoAFormItem label="Required time" error={errors['requiredByTime']}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <TimePicker
                      value={selectedTime ? selectedTime.tz("America/Edmonton") : null}
                      onChange={handleTimeChange}
                      ampm={false} // 24-hour format
                    />
                  </LocalizationProvider>
                </GoAFormItem>
              </div>
            </div>
          </div>
          <div className='input-form-item order-create-input'>
            <GoAFormItem label="Watchers" requirement="optional" error={watcherError}>
              <EmailPicker
                userSelectedList={selectedWatcherList}
                setUserSelectedList={handleSelectedWatcherListChange}
                setEmailValidationError={setWatcherError}
              />
            </GoAFormItem>
          </div>



          <div className='input-form-item-commands'>
            <div className="left width-auto header-button form-row">
              <GoAButtonGroup alignment="start">
                <GoAButton type="primary" onClick={handleNextStep}>
                  Next
                </GoAButton>
                <GoAButton
                  type="secondary"
                  onClick={() => {
                    navigate("/orderList");
                  }}
                >
                  Cancel
                </GoAButton>
                <GoAButton
                  type="tertiary"
                  onClick={handleClearAll}
                >
                  Clear all
                </GoAButton>
              </GoAButtonGroup>
            </div>
          </div>
        </div>
      </div>
      <GoAModal
        heading={modalHeading}
        calloutVariant={calloutVariant}
        open={showNotificationMessage}
        actions={
          <GoAButtonGroup alignment="end">
            <GoAButton onClick={() => setShowNotificationMessage(false)}>
              Okay
            </GoAButton>

          </GoAButtonGroup>
        }
      >
        <div dangerouslySetInnerHTML={{ __html: notificationMessage }} />
      </GoAModal>
    </>
  );
};

export default OrderNew;




function setFormChanged(arg0: boolean) {
  //throw new Error("Function not implemented.");
}

